import React, { useCallback, useEffect, useRef } from 'react';
import './_ContentEditableDiv.scss';

type Props = {
    value?: string;
    placeholder: string;
    onValueChange: (text: string) => void;
    onBlurred: (value: string) => void;
    isEditing: boolean;
    onEditing: (editing: boolean) => void;
    charLimit?: number;
};

export default function ContentEditableDiv({
    value,
    placeholder,
    onValueChange,
    charLimit,
    isEditing,
    onEditing,
    onBlurred,
}: Props) {
    const inputDiv = useRef() as React.MutableRefObject<HTMLDivElement>;

    /**
     * Blocks character entry when the character count limit is hit.
     */
    const onKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            const key: string = e.key;
            if (key === 'Enter' && inputDiv.current) {
                e.preventDefault();
                inputDiv.current.blur();
                return;
            }
            const value = e.currentTarget.textContent || '';
            const keyCode = e.keyCode;
            const allowedKeyCodes = [
                /* BACKSPACE */
                8 /* END */, 35 /* HOME */, 36 /* LEFT */, 37 /* UP */, 38 /* RIGHT*/, 39 /* DOWN */, 40 /* DEL*/, 46,
            ];
            const metaKeyCodes = [
                /* CTRL + A */
                65 /* CTRL + X */, 88 /* CTRL + C */, 67 /* CTRL + V */, 86 /* CTRL + Z */, 90,
            ];
            const eventIsAllowed =
                allowedKeyCodes.includes(keyCode) || ((e.ctrlKey || e.metaKey) && metaKeyCodes.includes(e.which));
            if (charLimit && value.length >= charLimit && !eventIsAllowed) {
                e.preventDefault();
            }
        },
        [charLimit, inputDiv],
    );

    const handleChange = useCallback(() => {
        const textContent = inputDiv.current.innerText || '';
        let newValue = textContent;
        if (charLimit) {
            newValue = newValue.substring(0, charLimit);
            if (textContent !== newValue) {
                inputDiv.current.innerText = newValue;
                const range = document.createRange();
                const sel = window.getSelection();
                range.setStart(inputDiv.current.childNodes[0], charLimit);
                range.collapse(true);

                sel?.removeAllRanges();
                sel?.addRange(range);
            }
        }

        if (!placeholder || newValue !== placeholder) {
            onValueChange(newValue); // in case you want to use it for anything while it changes
        }
    }, [charLimit, inputDiv, onValueChange, placeholder]);

    useEffect(() => {
        inputDiv.current.classList.add('force-redraw');
    }, [inputDiv]);

    useEffect(() => {
        if (isEditing) {
            inputDiv.current.focus();
            onValueChange(inputDiv.current.innerText);
        }
    }, [isEditing, inputDiv, onValueChange]);

    useEffect(() => {
        const handlePaste = (event) => {
            //prevent paste & grab the plain text
            //to prevent weird formatting when text is pasted
            if (inputDiv.current && inputDiv.current.contains(event.target)) {
                event.preventDefault();
                const text = event.clipboardData.getData('text/plain');
                document.execCommand('insertText', false, text);
            }
        };
        document.addEventListener('paste', handlePaste);
        return () => {
            document.removeEventListener('paste', handlePaste);
        };
    }, [inputDiv]);

    return (
        <div
            ref={inputDiv}
            className="content-editable-div"
            contentEditable={true}
            data-placeholder={placeholder}
            suppressContentEditableWarning={true}
            onKeyDown={onKeyDown}
            onInput={(e) => {
                if (e.currentTarget.innerHTML.trim() === '<br>') {
                    e.currentTarget.innerHTML = '';
                }
                handleChange();
            }}
            onClick={() => {
                onEditing(true);
            }}
            spellCheck={false}
            onBlur={(e) => {
                const value = e.currentTarget.textContent || '';
                if (value === '') {
                    e.currentTarget.textContent = placeholder;
                }
                onEditing(false);
                onBlurred(value);
            }}
            onFocus={(e) => {
                onEditing(true);
                if (e.target.textContent !== '' && e.target.textContent === placeholder) {
                    setTimeout(() => {
                        e.target.innerHTML = '';
                        handleChange();
                    }, 1);
                }
            }}
            onPaste={() => {
                setTimeout(() => {
                    handleChange();
                }, 1);
            }}
        >
            {value ?? placeholder}
        </div>
    );
}
